import React, { useCallback, useState } from "react";
import DashboardWidgetContainer from "../../../containers/DashboardWidget/DashboardWidgetContainer";
import {
  Box,
  Divider,
  TextField,
  MenuItem,
  useTheme,
  Typography
} from "@mui/material";
import { widgetKeys } from "../../../constants/index";
import UpcomingIltList from "./UpcomingIltList";
import { daysFromToday } from "../../../utils/datetime";
import { useTranslation } from "react-i18next";
import InPersonAssignmentModal from "../../InPersonAssignment/InPersonAssignmentModal";
import SearchBar from "../../SearchBar/SearchBar";
import { debounce } from "lodash";
import { CircularProgress } from "@mui/material";
import Skeleton from "../../Skeleton/Skeleton";
import useConstant from "use-constant";
import { PropTypes } from "prop-types";
import { useCourses } from "../../../hooks/learningCenter/myLibrary/courses";
import { VirtualScrollingProvider } from "../../../context/VirtualScrollingContext";
import { WindowSizeProvider } from "../../../context/WindowSizeContext";

const filterOptions = [
  { label: "dateGroups.allUpcomingTrainings", value: "" },
  { label: "courseTypes.webinar", value: "webinar" },
  { label: "courseTypes.virtualClassroom", value: "virtual" },
  { label: "courseTypes.inPerson", value: "ilt" }
];

const dateGroups = [
  {
    header: "dateGroups.startsSevenDays",
    from: Number.MIN_SAFE_INTEGER,
    to: 7
  },
  { header: "dateGroups.startsThirtyDays", from: 8, to: 30 },
  { header: "dateGroups.startsSixtyDays", from: 31, to: 60 },
  { header: "dateGroups.upcoming", from: 61, to: Number.MAX_SAFE_INTEGER }
];

const addDateGroupLabelToCourses = courses => {
  return courses.map(course => {
    const daysDiff = daysFromToday(course.scheduledDate);
    const dateLabel = dateGroups.find(
      ({ from, to }) => daysDiff >= from && daysDiff <= to
    )?.header;
    return { ...course, dateLabel };
  });
};

const UpcomingIltSkeleton = () => {
  const listSize = 3;
  return (
    <Box display="flex" flexDirection="column" data-qa="upcoming-ilt-skeleton">
      <Skeleton loading height={32} />
      {[...Array(listSize)].map((v, index) => (
        <Box display="flex" flexDirection="column" key={index} pt={2}>
          <Box display="flex" pb={2} mx={{ xs: 0, sm: 2 }}>
            <Skeleton loading height={72} width={{ xs: 0, sm: 72 }} />
            <Skeleton
              variant="circular"
              loading
              height={24}
              width={{ xs: 0, sm: 24 }}
              ml={{ xs: 0, sm: 3 }}
            />
            <Box
              display="flex"
              flexDirection="column"
              pl={{ xs: 0, sm: 2 }}
              flex={1}
            >
              <Skeleton loading height={22} width="60%" mb={0.5} />
              <Skeleton loading height={14} width={260} mt={1} ml={3} />
              <Skeleton loading height={14} width={230} mt={1} ml={3} />
              <Skeleton loading height={14} width={260} mt={1} ml={3} />
            </Box>
            <Skeleton loading width={128} height={36} mt={3} />
          </Box>

          {index !== listSize - 1 && <Divider />}
        </Box>
      ))}
    </Box>
  );
};

const UpcomingIlt = ({ dashboard }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [filterOption, setFilterOption] = useState(filterOptions[0]);
  const [search, setSearch] = useState("");
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showGetDirectionsModal, setShowGetDirectionsModal] = useState(false);

  const { fetchCourses } = useCourses();

  const handleFilterChange = ({ target: { value: selectedOption } }) => {
    setFilterOption(selectedOption);
  };

  const handleSearch = useConstant(() =>
    debounce(newSearchValue => setSearch(newSearchValue), 500)
  );

  const handleGetDirectionModalClose = () => {
    setShowGetDirectionsModal(false);
    setSelectedCourse(null);
  };

  const openInPersonAssignmentModal = course => {
    setSelectedCourse(course);
    setShowGetDirectionsModal(true);
  };

  const handleFetchCourses = useCallback(
    async page => {
      const { data, totalCount } = await fetchCourses({
        params: { type: "ilt" },
        externalFilters: { search, courseType: filterOption.value },
        page
      });
      return { data: addDateGroupLabelToCourses(data), totalCount };
    },
    [search, filterOption.value]
  );

  const isFetched = fetchCount => fetchCount > 0;
  const hasNoUpcomingIlts = (fetchCount, hasCourses) =>
    !hasCourses && isFetched(fetchCount);
  const hasNoSearchResults = (fetchCount, hasCourses, coursesCount) =>
    isFetched(fetchCount) && hasCourses && coursesCount === 0;

  return (
    <WindowSizeProvider>
      <VirtualScrollingProvider fetchData={handleFetchCourses} pageSize={8}>
        {({ isNextPageLoading, fetchCount, hasResults, totalCount }) => (
          <DashboardWidgetContainer
            dashboard={dashboard}
            title={t("upcomingIltWidget.title")}
            widgetKey={widgetKeys.ilt}
            widgetDataLoading={isNextPageLoading}
            headerAction={
              fetchCount > 0 && isNextPageLoading ? (
                <Box mr={2} display="flex" alignItems="center">
                  <CircularProgress size={22} color="secondary" />
                </Box>
              ) : null
            }
            subheader={
              <Box
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                flexWrap="wrap"
                mb={-2.5}
              >
                <Box
                  width={{ xs: "100%", sm: "60%" }}
                  mr={{ xs: 0, sm: 2 }}
                  mb={2.5}
                  minWidth={222}
                >
                  <SearchBar
                    background={theme.palette.inputBackground}
                    searchIconPosition="end"
                    placeholder={t("search")}
                    onSearchInput={handleSearch}
                    filled
                  />
                </Box>
                <Box mb={2.5} flex={1} minWidth={222}>
                  <TextField
                    select
                    fullWidth
                    value={filterOption}
                    onChange={handleFilterChange}
                    variant="filled"
                    InputProps={{ disableUnderline: true }}
                    SelectProps={{
                      renderValue: value => t(value.label)
                    }}
                    hiddenLabel
                  >
                    {filterOptions.map(option => (
                      <MenuItem value={option} key={option.value}>
                        {t(option.label)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Box>
            }
          >
            {!isFetched(fetchCount) ? (
              <UpcomingIltSkeleton />
            ) : (
              <UpcomingIltList
                openInPersonAssignmentModal={openInPersonAssignmentModal}
              />
            )}
            {hasNoSearchResults(fetchCount, hasResults, totalCount) && (
              <Typography sx={{ mt: 1.5 }}>
                {t("upcomingIltWidget.noResultsFound")}
              </Typography>
            )}
            {hasNoUpcomingIlts(fetchCount, hasResults) && (
              <Typography sx={{ mt: 1.5 }} variant="subtitle1" color="primary">
                {t("upcomingIltWidget.noUpcomingIltsMsg")}
              </Typography>
            )}
            {selectedCourse && (
              <InPersonAssignmentModal
                assignment={selectedCourse}
                open={showGetDirectionsModal}
                onClose={handleGetDirectionModalClose}
              />
            )}
          </DashboardWidgetContainer>
        )}
      </VirtualScrollingProvider>
    </WindowSizeProvider>
  );
};

UpcomingIlt.propTypes = {
  dashboard: PropTypes.string
};

export default UpcomingIlt;
